import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Button,
  Collapse,
  Grid,
  Divider,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ScienceIcon from "@mui/icons-material/Science";
import PersonIcon from "@mui/icons-material/Person";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import PublicIcon from "@mui/icons-material/Public";
import StorageIcon from "@mui/icons-material/Storage";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import EligibilityPlainText from "../health/EligibilityPlainText";

const CITY_TO_IATA = {
  boston: "BOS", "new york": "JFK", "new york city": "JFK", nyc: "JFK",
  philadelphia: "PHL", baltimore: "BWI", washington: "DCA", atlanta: "ATL",
  miami: "MIA", orlando: "MCO", chicago: "ORD", cleveland: "CLE",
  detroit: "DTW", minneapolis: "MSP", rochester: "RST", "mayo clinic": "RST",
  houston: "IAH", dallas: "DFW", austin: "AUS", denver: "DEN",
  phoenix: "PHX", "los angeles": "LAX", "san francisco": "SFO",
  "san diego": "SAN", seattle: "SEA", portland: "PDX", "salt lake city": "SLC",
  nashville: "BNA", indianapolis: "IND", columbus: "CMH", pittsburgh: "PIT",
  "st. louis": "STL", "saint louis": "STL", "kansas city": "MCI",
  "new orleans": "MSY", tampa: "TPA", jacksonville: "JAX", charlotte: "CLT",
  raleigh: "RDU", richmond: "RIC", hartford: "BDL", "new haven": "HVN",
  stanford: "SFO", "palo alto": "SFO", "ann arbor": "DTW", durham: "RDU",
  "chapel hill": "RDU", memphis: "MEM", birmingham: "BHM", omaha: "OMA",
  milwaukee: "MKE", cincinnati: "CVG", louisville: "SDF", sacramento: "SMF",
  "san jose": "SJC", oakland: "OAK", bethesda: "DCA", nih: "DCA",
};

const getBestTrialLocation = (trial) => {
  const locations = trial.locations;
  if (!locations || locations.length === 0) return null;
  const locationList = Array.isArray(locations) ? locations : [locations];
  for (const loc of locationList) {
    const locStr = (typeof loc === "string" ? loc : loc?.city || loc?.name || "").toLowerCase();
    for (const [city, iata] of Object.entries(CITY_TO_IATA)) {
      if (locStr.includes(city)) {
        const displayCity = typeof loc === "string" ? loc : loc?.city || loc?.name || loc;
        return { iata, city: displayCity };
      }
    }
  }
  return null;
};

const TrialCard = ({ trial, source = "internal" }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const trialSource = trial.source || source;
  const borderColor = trialSource === "external" ? "#1976d2" : "#2e7d32";

  const getStatusColor = (status) => {
    const s = status?.toUpperCase() || "";
    if (s.includes("RECRUITING") && !s.includes("NOT")) return "success";
    if (s.includes("ACTIVE")) return "warning";
    if (s.includes("COMPLETED")) return "info";
    if (s.includes("NOT YET") || s.includes("NOT_YET")) return "error";
    return "default";
  };

  const formatStatus = (status) => status?.replace(/_/g, " ") || "Unknown";

  const formatPhase = (phase) => {
    if (!phase || phase === "NA") return "N/A";
    if (phase === "EARLY_PHASE1") return "Early Phase 1";
    return phase.replace("PHASE", "Phase ");
  };

  const handleBookTravel = () => {
    let locationDetails = { facility: "", city: "", state: "", address: "" };
    if (trial.locations && trial.locations.length > 0) {
      const first = trial.locations[0];
      if (typeof first === "object") {
        locationDetails = {
          facility: first.facility || first.name || "",
          city: first.city || "",
          state: first.state || "",
          address: first.address || "",
        };
      } else if (typeof first === "string") {
        locationDetails.city = first;
      }
    }
    navigate("/travel/book", {
      state: {
        trial: {
          nctId: trial.nctId || trial.id || "",
          officialTitle: trial.title || trial.officialTitle || "",
          ...locationDetails,
        },
      },
    });
  };

  const SourceBadge = () =>
    trialSource === "external" ? (
      <Chip icon={<PublicIcon />} label="ClinicalTrials.gov" size="small" color="primary" variant="outlined" />
    ) : (
      <Chip icon={<StorageIcon />} label="Our Database" size="small" color="success" variant="outlined" />
    );

  return (
    <Card elevation={3} sx={{ "&:hover": { boxShadow: 6 }, border: `2px solid ${borderColor}` }}>
      <CardContent>
        {/* Source Badge + Edit */}
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <SourceBadge />
          {trialSource === "internal" && trial.id && (
            <Button size="small" variant="outlined" color="primary"
              onClick={() => navigate(`/admin/trials/edit/${trial.id}`)}
              startIcon={<EditIcon />}>Edit Trial</Button>
          )}
        </Box>

        {/* Title + Status */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 2 }}>
          <Typography variant="h6" component="h3" sx={{ flex: 1, pr: 2 }}>
            {trial.title || trial.officialTitle}
          </Typography>
          <Chip label={formatStatus(trial.status)} color={getStatusColor(trial.status)} size="small" />
        </Box>

        {/* NCT ID */}
        <Typography variant="body2" color="primary" sx={{ mb: 2, fontWeight: "bold" }}>
          NCT ID: {trial.nctId}
        </Typography>

        {/* Info Grid */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ScienceIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Phase</Typography>
                <Typography variant="body2" fontWeight="medium">{formatPhase(trial.phase)}</Typography>
              </Box>
            </Box>
          </Grid>
          {trial.sponsor && (
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Sponsor</Typography>
                  <Typography variant="body2" fontWeight="medium">{trial.sponsor}</Typography>
                </Box>
              </Box>
            </Grid>
          )}
          {trial.startDate && (
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarTodayIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Start Date</Typography>
                  <Typography variant="body2" fontWeight="medium">{trial.startDate}</Typography>
                </Box>
              </Box>
            </Grid>
          )}
          {trial.locations && (
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocationOnIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Locations</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {Array.isArray(trial.locations) ? `${trial.locations.length} site(s)` : trial.locations}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Summary */}
        {(trial.briefSummary || trial.description) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {(trial.briefSummary || trial.description).length > 300
                ? `${(trial.briefSummary || trial.description).substring(0, 300)}...`
                : trial.briefSummary || trial.description}
            </Typography>
          </>
        )}

        {/* Internal type chips */}
        {trialSource === "internal" && trial.ckdStage && (
          <Box sx={{ mt: 2 }}>
            <Chip label={`Stage: ${trial.ckdStage}`} size="small" color="primary" sx={{ mr: 1 }} />
            {trial.targetOrgan && <Chip label={`Target: ${trial.targetOrgan}`} size="small" color="secondary" />}
          </Box>
        )}

        {/* External location chips */}
        {trialSource === "external" && Array.isArray(trial.locations) && trial.locations.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Trial Locations:</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {trial.locations.slice(0, 3).map((loc, i) => (
                <Chip key={i} icon={<LocationOnIcon />} label={loc} size="small" variant="outlined" />
              ))}
              {trial.locations.length > 3 && (
                <Chip label={`+${trial.locations.length - 3} more`} size="small" variant="outlined" />
              )}
            </Box>
          </>
        )}
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button size="small" variant="outlined" onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />} fullWidth>
          {expanded ? "Hide Details" : "View Details"}
        </Button>
        <Button size="small" variant="contained" onClick={handleBookTravel}
          startIcon={<FlightTakeoffIcon />} fullWidth
          sx={{
            background: "linear-gradient(135deg, #0f3d6e 0%, #1B6CA8 100%)",
            color: "white", fontWeight: "bold",
            "&:hover": { background: "linear-gradient(135deg, #0a2d52 0%, #155a8a 100%)" },
          }}>
          Book Travel
        </Button>
      </CardActions>

      {/* Expanded Details */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider />
        <CardContent sx={{ bgcolor: "#f5f5f5" }}>
          <Grid container spacing={3}>
            {trial.detailedDescription && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">Detailed Description</Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: "white" }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{trial.detailedDescription}</Typography>
                </Paper>
              </Grid>
            )}
            {trial.eligibilityCriteria && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">Eligibility Criteria</Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: "white" }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{trial.eligibilityCriteria}</Typography>
                </Paper>
              </Grid>
            )}
            {trial.inclusionCriteria && (
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="success.main">Inclusion Criteria</Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: "white" }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{trial.inclusionCriteria}</Typography>
                </Paper>
              </Grid>
            )}
            {trial.exclusionCriteria && (
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="error.main">Exclusion Criteria</Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: "white" }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{trial.exclusionCriteria}</Typography>
                </Paper>
              </Grid>
            )}

            {/* Plain English Eligibility Translation */}
{trialSource === "internal" && trial.id && (
  <Grid item xs={12}>
    <Divider sx={{ my: 2 }} />
    <Typography variant="h6" gutterBottom color="primary">
      🤝 Am I Eligible? — Plain English
    </Typography>
    <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8faff", border: "1px solid #e0e7ff", borderRadius: 2 }}>
      <EligibilityPlainText
        trialId={trial.id}
        nctId={trial.nctId}
        trialName={trial.title || trial.officialTitle}
        defaultOpen={true}
        compact={false}
      />
    </Paper>
  </Grid>
)}

            {/* Study Design */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom color="primary">Study Design & Information</Typography>
              <Grid container spacing={2}>
                {[
                  ["Study Type", trial.studyType],
                  ["Enrollment", trial.enrollment],
                  ["Start Date", trial.startDate],
                  ["Completion Date", trial.completionDate],
                  ["Gender", trial.gender],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <Grid item xs={12} sm={6} md={4} key={label}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{label}:</strong> {value}
                    </Typography>
                  </Grid>
                ))}
                {trial.acceptsHealthyVolunteers !== undefined && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Healthy Volunteers:</strong> {trial.acceptsHealthyVolunteers ? "Yes" : "No"}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
             
            {/* Expanded action buttons */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", gap: 2 }}>
                {trialSource === "internal" && trial.id && (
                  <Button variant="contained" color="success" fullWidth
                    onClick={() => navigate(`/admin/trials/edit/${trial.id}`)}
                    startIcon={<EditIcon />}>Edit Trial Data</Button>
                )}
                {trial.nctId && (
                  <Button variant="contained" color={trialSource === "external" ? "primary" : "success"}
                    href={`https://clinicaltrials.gov/study/${trial.nctId}`}
                    target="_blank" rel="noopener noreferrer"
                    endIcon={<OpenInNewIcon />} fullWidth>
                    View on ClinicalTrials.gov
                  </Button>
                )}
                <Button variant="contained" onClick={handleBookTravel}
                  startIcon={<FlightTakeoffIcon />} fullWidth
                  sx={{
                    background: "linear-gradient(135deg, #0f3d6e 0%, #1B6CA8 100%)",
                    color: "white", fontWeight: "bold",
                    "&:hover": { background: "linear-gradient(135deg, #0a2d52 0%, #155a8a 100%)" },
                  }}>
                  Book Travel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default TrialCard;